import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import {
  CheckoutFailReason,
  ContactType,
  PromoFailReason,
} from "ipctg-inflow-types/InFlowEnums"
import { ParkPromoPOSTSuccessResponse } from "ipctg-inflow-types/InFlowParkAPI"

import api from "@/utilities/api"

import Cart from "@/globalComponents/organisms/Cart"
import { ParkCheckoutError, ParkPromoError } from "@/utilities/expectedErrors"
import { parkCheckoutQueryOptions } from "@/utilities/hooks/queries/park"
import { log } from "@/utilities/logging"

import { ContactSearchParams, validateContactSearchParams } from "./route"

interface CartSearchParams extends ContactSearchParams {
  rateID: string
}

export const Route = createFileRoute("/qr/$qrLookupID/park/$epoch/cart")({
  staticData: {
    pageName: "Cart",
  },
  validateSearch: (search: Record<string, unknown>): CartSearchParams => {
    const rateID = (search.rateID as string) || ""
    return {
      ...validateContactSearchParams(search),
      rateID,
    }
  },
  beforeLoad: ({ search }) => {
    const { rateID, contactType, email, phone } = search
    if (!rateID) {
      log({
        level: "error",
        flow: "park",
        scope: "guard.rate",
        message: "No rate information found on /cart",
        meta: { search },
      })
      throw new ParkCheckoutError(CheckoutFailReason.InvalidRate)
    }

    if (
      (contactType === ContactType.Email && !email) ||
      (contactType === ContactType.Phone && !phone)
    ) {
      log({
        level: "error",
        flow: "park",
        scope: "guard.contact",
        message: `No contact information found on /cart for ${contactType}`,
        meta: { search },
      })
      throw new ParkCheckoutError(CheckoutFailReason.InvalidRequestFormat)
    }
  },
  component: RouteComponent,
})

function RouteComponent() {
  const navigate = Route.useNavigate()
  const params = Route.useParams()
  const { rateID, ...search } = Route.useSearch()
  const queryClient = useQueryClient()

  const {
    data: checkoutData,
    refetch,
    isRefetching,
  } = useSuspenseQuery(
    parkCheckoutQueryOptions({
      rateID,
      epoch: params.epoch,
    })
  )

  const applyPromotionMutation = useMutation({
    mutationFn: applyPromotionMutationFn,
    onSuccess: applyPromoOnSuccess,
  })

  const onCheckoutMutation = useMutation({
    mutationFn: onCheckoutMutationFn,
  })

  return (
    <Cart
      productType="rate"
      transaction={checkoutData.transactionDetails}
      mutations={{
        applyPromotion: applyPromotionMutation,
        resetPromotion: {
          mutate: () => refetch(),
          isPending: isRefetching,
        },
        onGoNext: onCheckoutMutation,
      }}
    />
  )

  async function applyPromotionMutationFn(promoCode: string) {
    if (!promoCode) {
      throw new ParkPromoError(PromoFailReason.InvalidCode)
    }

    const rsp = await api.park.promo.POST({
      promoCode,
    })

    if ("failReason" in rsp) {
      throw new ParkPromoError(rsp.failReason)
    }

    return rsp
  }

  function applyPromoOnSuccess(data: ParkPromoPOSTSuccessResponse) {
    queryClient.setQueryData(
      parkCheckoutQueryOptions({
        rateID,
        epoch: params.epoch,
      }).queryKey,
      data
    )
  }

  //we dont actually care if this throws or not
  async function onCheckoutMutationFn(promoCode: string) {
    if (promoCode) {
      try {
        await api.park.promo.POST({
          promoCode,
        })
      } catch (e) {
        console.error(e)
      }
    }
    await navigate({
      to: "/qr/$qrLookupID/park/$epoch/checkout",
      search,
    })
  }
}
